import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import {
  Save,
  Eye,
  Monitor,
  Smartphone,
  Tablet,
  Undo,
  Redo,
  Settings,
  Palette,
  Type,
  Image,
  Layout,
  Plus,
  Trash2,
  Copy,
  ArrowUp,
  ArrowDown,
  X,
  Check,
  Globe,
  ChevronLeft
} from 'lucide-react';
import toast from 'react-hot-toast';

const componentTypes = [
  { id: 'hero', name: 'Hero Section', icon: Layout },
  { id: 'text', name: 'Text Block', icon: Type },
  { id: 'image', name: 'Image', icon: Image },
  { id: 'button', name: 'Button', icon: Plus },
  { id: 'divider', name: 'Divider', icon: Layout },
];

const VisualEditor = () => {
  const { websiteId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [website, setWebsite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState('desktop');
  const [activeTab, setActiveTab] = useState('components');
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [components, setComponents] = useState([
    {
      id: '1',
      type: 'hero',
      content: {
        headline: 'Welcome to Our Website',
        subheadline: 'We provide the best services for you',
        buttonText: 'Get Started',
        buttonLink: '#'
      },
      styles: {
        backgroundColor: '#3B82F6',
        textColor: '#FFFFFF',
        padding: '80px 40px',
        textAlign: 'center'
      }
    },
    {
      id: '2',
      type: 'text',
      content: {
        text: 'This is a sample text block. You can edit this content to match your needs.'
      },
      styles: {
        fontSize: '16px',
        color: '#374151',
        padding: '40px',
        textAlign: 'left'
      }
    }
  ]);

  useEffect(() => {
    if (websiteId && user) {
      fetchWebsite();
    }
  }, [websiteId, user]);

  const fetchWebsite = async () => {
    try {
      setLoading(true);
      const docRef = doc(db, 'websites', websiteId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        if (data.userId !== user.uid) {
          toast.error('You do not have permission to edit this website');
          navigate('/site/websites');
          return;
        }
        setWebsite(data);
      } else {
        toast.error('Website not found');
        navigate('/site/websites');
      }
    } catch (error) {
      console.error('Error fetching website:', error);
      toast.error('Failed to load website');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await updateDoc(doc(db, 'websites', websiteId), {
        components: components,
        updatedAt: serverTimestamp()
      });
      toast.success('Changes saved successfully!');
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    try {
      await updateDoc(doc(db, 'websites', websiteId), {
        status: 'published',
        updatedAt: serverTimestamp()
      });
      toast.success('Website published successfully!');
    } catch (error) {
      toast.error('Failed to publish');
    }
  };

  const addComponent = (type) => {
    const newComponent = {
      id: Date.now().toString(),
      type,
      content: getDefaultContent(type),
      styles: getDefaultStyles(type)
    };
    setComponents([...components, newComponent]);
    toast.success('Component added');
  };

  const getDefaultContent = (type) => {
    switch (type) {
      case 'hero':
        return { headline: 'New Hero Section', subheadline: 'Add your subheadline', buttonText: 'Click Me' };
      case 'text':
        return { text: 'Enter your text here...' };
      case 'image':
        return { src: '', alt: 'Image' };
      case 'button':
        return { text: 'Button', link: '#' };
      default:
        return {};
    }
  };

  const getDefaultStyles = (type) => {
    switch (type) {
      case 'hero':
        return { backgroundColor: '#F3F4F6', textColor: '#111827', padding: '60px 40px', textAlign: 'center' };
      case 'text':
        return { fontSize: '16px', color: '#374151', padding: '20px', textAlign: 'left' };
      case 'image':
        return { width: '100%', borderRadius: '8px' };
      case 'button':
        return { backgroundColor: '#3B82F6', color: '#FFFFFF', padding: '12px 24px', borderRadius: '8px' };
      default:
        return {};
    }
  };

  const removeComponent = (id) => {
    setComponents(components.filter(c => c.id !== id));
    setSelectedComponent(null);
  };

  const moveComponent = (id, direction) => {
    const index = components.findIndex(c => c.id === id);
    if (direction === 'up' && index > 0) {
      const newComponents = [...components];
      [newComponents[index], newComponents[index - 1]] = [newComponents[index - 1], newComponents[index]];
      setComponents(newComponents);
    } else if (direction === 'down' && index < components.length - 1) {
      const newComponents = [...components];
      [newComponents[index], newComponents[index + 1]] = [newComponents[index + 1], newComponents[index]];
      setComponents(newComponents);
    }
  };

  const renderComponent = (component) => {
    const isSelected = selectedComponent?.id === component.id;
    
    switch (component.type) {
      case 'hero':
        return (
          <div
            style={{
              backgroundColor: component.styles.backgroundColor,
              color: component.styles.textColor,
              padding: component.styles.padding,
              textAlign: component.styles.textAlign
            }}
            className={`relative ${isSelected ? 'ring-2 ring-primary-500' : ''}`}
            onClick={() => setSelectedComponent(component)}
          >
            <h1 className="text-4xl font-bold mb-4">{component.content.headline}</h1>
            <p className="text-xl mb-6">{component.content.subheadline}</p>
            <button
              style={{
                backgroundColor: component.styles.textColor,
                color: component.styles.backgroundColor
              }}
              className="px-6 py-3 rounded-lg font-medium"
            >
              {component.content.buttonText}
            </button>
          </div>
        );
      
      case 'text':
        return (
          <div
            style={{
              fontSize: component.styles.fontSize,
              color: component.styles.color,
              padding: component.styles.padding,
              textAlign: component.styles.textAlign
            }}
            className={`relative ${isSelected ? 'ring-2 ring-primary-500' : ''}`}
            onClick={() => setSelectedComponent(component)}
          >
            <p>{component.content.text}</p>
          </div>
        );
      
      case 'image':
        return (
          <div
            style={{ padding: '20px' }}
            className={`relative ${isSelected ? 'ring-2 ring-primary-500' : ''}`}
            onClick={() => setSelectedComponent(component)}
          >
            {component.content.src ? (
              <img
                src={component.content.src}
                alt={component.content.alt}
                style={{ width: component.styles.width, borderRadius: component.styles.borderRadius }}
              />
            ) : (
              <div className="bg-gray-200 h-48 rounded-lg flex items-center justify-center">
                <Image className="w-12 h-12 text-gray-400" />
              </div>
            )}
          </div>
        );
      
      case 'button':
        return (
          <div
            style={{ padding: '20px', textAlign: 'center' }}
            className={`relative ${isSelected ? 'ring-2 ring-primary-500' : ''}`}
            onClick={() => setSelectedComponent(component)}
          >
            <button
              style={{
                backgroundColor: component.styles.backgroundColor,
                color: component.styles.color,
                padding: component.styles.padding,
                borderRadius: component.styles.borderRadius
              }}
              className="font-medium"
            >
              {component.content.text}
            </button>
          </div>
        );
      
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* Top Bar */}
      <header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/site/websites')}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="font-semibold text-gray-900">{website?.name}</h1>
            <p className="text-xs text-gray-500">
              {website?.status === 'published' ? 'Published' : 'Draft'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Preview Mode */}
          <div className="flex items-center bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setPreviewMode('desktop')}
              className={`p-2 rounded ${previewMode === 'desktop' ? 'bg-white shadow-sm' : 'text-gray-500'}`}
            >
              <Monitor className="w-4 h-4" />
            </button>
            <button
              onClick={() => setPreviewMode('tablet')}
              className={`p-2 rounded ${previewMode === 'tablet' ? 'bg-white shadow-sm' : 'text-gray-500'}`}
            >
              <Tablet className="w-4 h-4" />
            </button>
            <button
              onClick={() => setPreviewMode('mobile')}
              className={`p-2 rounded ${previewMode === 'mobile' ? 'bg-white shadow-sm' : 'text-gray-500'}`}
            >
              <Smartphone className="w-4 h-4" />
            </button>
          </div>

          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save'}
          </button>

          <button
            onClick={handlePublish}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700"
          >
            <Globe className="w-4 h-4" />
            Publish
          </button>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar - Components */}
        <aside className="w-64 bg-white border-r border-gray-200 flex flex-col">
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('components')}
              className={`flex-1 py-3 text-sm font-medium ${
                activeTab === 'components' ? 'text-primary-600 border-b-2 border-primary-600' : 'text-gray-500'
              }`}
            >
              Components
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              className={`flex-1 py-3 text-sm font-medium ${
                activeTab === 'settings' ? 'text-primary-600 border-b-2 border-primary-600' : 'text-gray-500'
              }`}
            >
              Settings
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            {activeTab === 'components' ? (
              <div className="space-y-2">
                {componentTypes.map((type) => (
                  <button
                    key={type.id}
                    onClick={() => addComponent(type.id)}
                    className="w-full flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-left"
                  >
                    <type.icon className="w-5 h-5 text-gray-500" />
                    <span className="font-medium text-gray-700">{type.name}</span>
                    <Plus className="w-4 h-4 ml-auto text-gray-400" />
                  </button>
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                {selectedComponent ? (
                  <>
                    <div className="flex items-center justify-between">
                      <h3 className="font-medium text-gray-900">Edit Component</h3>
                      <button
                        onClick={() => setSelectedComponent(null)}
                        className="p-1 text-gray-400 hover:text-gray-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    
                    {/* Component Editor */}
                    <div className="space-y-3">
                      {Object.entries(selectedComponent.content).map(([key, value]) => (
                        <div key={key}>
                          <label className="block text-xs font-medium text-gray-700 mb-1 capitalize">
                            {key}
                          </label>
                          <input
                            type="text"
                            value={value}
                            onChange={(e) => {
                              const updated = components.map(c =>
                                c.id === selectedComponent.id
                                  ? { ...c, content: { ...c.content, [key]: e.target.value } }
                                  : c
                              );
                              setComponents(updated);
                              setSelectedComponent({ ...selectedComponent, content: { ...selectedComponent.content, [key]: e.target.value } });
                            }}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                          />
                        </div>
                      ))}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 pt-4 border-t border-gray-200">
                      <button
                        onClick={() => moveComponent(selectedComponent.id, 'up')}
                        className="flex-1 p-2 bg-gray-100 rounded-lg hover:bg-gray-200"
                      >
                        <ArrowUp className="w-4 h-4 mx-auto" />
                      </button>
                      <button
                        onClick={() => moveComponent(selectedComponent.id, 'down')}
                        className="flex-1 p-2 bg-gray-100 rounded-lg hover:bg-gray-200"
                      >
                        <ArrowDown className="w-4 h-4 mx-auto" />
                      </button>
                      <button
                        onClick={() => removeComponent(selectedComponent.id)}
                        className="flex-1 p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200"
                      >
                        <Trash2 className="w-4 h-4 mx-auto" />
                      </button>
                    </div>
                  </>
                ) : (
                  <p className="text-gray-500 text-center py-8">
                    Select a component to edit
                  </p>
                )}
              </div>
            )}
          </div>
        </aside>

        {/* Preview Area */}
        <main className="flex-1 overflow-y-auto bg-gray-100 p-8">
          <div
            className={`mx-auto bg-white shadow-lg transition-all duration-300 ${
              previewMode === 'desktop' ? 'max-w-5xl' :
              previewMode === 'tablet' ? 'max-w-2xl' :
              'max-w-sm'
            }`}
          >
            {components.map((component) => (
              <div key={component.id} className="relative group">
                {renderComponent(component)}
              </div>
            ))}
            
            {components.length === 0 && (
              <div className="p-16 text-center">
                <Layout className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Start Building</h3>
                <p className="text-gray-500 mb-4">Add components from the sidebar to get started</p>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default VisualEditor;
